/**
 * GDAP Relationship detail page.
 * Shows relationship summary, approval URL, access assignments, and drift status.
 */

import { useState, useMemo } from "react";
import { useParams, useNavigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  Shield,
  Copy,
  ExternalLink,
  AlertTriangle,
  CheckCircle,
  Clock,
  XCircle,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { ENTRA_ROLE_DISPLAY_NAMES } from "@gdapwif/shared";
import { GlassCard } from "@/components/sc/glass-card";
import { StatusBadge } from "@/components/sc/status-badge";
import {
  useGdapRelationshipDetail,
  useTerminateRelationship,
  type GdapRelationshipDetail,
} from "@/hooks/use-gdap";

function LoadingSpinner({ resource }: { readonly resource: string }) {
  return (
    <GlassCard className="flex items-center justify-center py-16">
      <div className="text-center">
        <div className="mx-auto mb-2 h-6 w-6 rounded-full bg-sc-primary animate-sc-pulse" />
        <p className="text-body-sm text-sc-text-muted">
          Loading {resource}...
        </p>
      </div>
    </GlassCard>
  );
}

function InfoRow({
  label,
  value,
  mono,
}: {
  readonly label: string;
  readonly value: string | number | null | undefined;
  readonly mono?: boolean;
}) {
  return (
    <div className="flex justify-between py-2 border-b border-sc-border/50 last:border-b-0">
      <span className="text-body-sm text-sc-text-muted">{label}</span>
      <span
        className={`text-body-sm text-sc-text font-medium ${mono ? "font-mono text-label" : ""}`}
      >
        {value ?? "—"}
      </span>
    </div>
  );
}

function StatusIcon({ status }: { readonly status: string }) {
  switch (status) {
    case "Active":
      return <CheckCircle size={16} className="text-green-500" />;
    case "Approving":
    case "Creating":
      return <Clock size={16} className="text-blue-400" />;
    case "Expired":
    case "Terminated":
    case "CreationFailed":
      return <XCircle size={16} className="text-sc-danger" />;
    default:
      return null;
  }
}

export function GdapDetailPage() {
  const { relationshipId } = useParams({ strict: false }) as {
    relationshipId: string;
  };
  const navigate = useNavigate();
  const detailQuery = useGdapRelationshipDetail(relationshipId);
  const terminateMutation = useTerminateRelationship();

  const rel = detailQuery.data;

  if (detailQuery.isLoading) {
    return <LoadingSpinner resource="relationship" />;
  }

  if (detailQuery.isError || !rel) {
    return (
      <GlassCard className="text-center py-16">
        <Shield size={48} className="mx-auto mb-4 text-sc-text-muted" />
        <h2 className="text-h3 font-display text-sc-navy mb-2">
          Relationship not found
        </h2>
        <p className="text-body-sm text-sc-text-muted mb-4">
          The relationship may have been removed or is no longer available.
        </p>
        <button
          onClick={() => void navigate({ to: "/gdap", search: { createFor: undefined } })}
          className="text-body-sm text-sc-primary hover:underline"
        >
          Back to GDAP Relationships
        </button>
      </GlassCard>
    );
  }

  function handleTerminate() {
    if (
      !confirm(
        `Are you sure you want to terminate the relationship with ${rel!.customerTenantName}? This cannot be undone.`,
      )
    )
      return;

    terminateMutation.mutate(rel!.id, {
      onSuccess: () => {
        toast.success("Relationship termination initiated");
        void navigate({ to: "/gdap", search: { createFor: undefined } });
      },
      onError: () => {
        toast.error("Failed to terminate relationship");
      },
    });
  }

  function copyToClipboard(text: string, label: string) {
    void navigator.clipboard.writeText(text).then(() => {
      toast.success(`${label} copied to clipboard`);
    });
  }

  return (
    <div className="space-y-6">
      {/* Back nav + header */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => void navigate({ to: "/gdap", search: { createFor: undefined } })}
          className="flex items-center gap-1 text-body-sm text-sc-text-muted hover:text-sc-text transition-colors"
        >
          <ArrowLeft size={16} />
          Back to Relationships
        </button>
        {(rel.status === "Active" || rel.status === "Approving") && (
          <button
            onClick={handleTerminate}
            disabled={terminateMutation.isPending}
            className="px-3 py-1.5 rounded-btn text-body-sm font-medium text-sc-danger border border-sc-danger/30 hover:bg-sc-danger/10 transition-colors disabled:opacity-50"
          >
            {terminateMutation.isPending ? "Terminating..." : "Terminate"}
          </button>
        )}
      </div>

      {/* Approval URL banner */}
      {rel.status === "Approving" && rel.approvalUrl && (
        <ApprovalBanner
          approvalUrl={rel.approvalUrl}
          onCopy={() => copyToClipboard(rel.approvalUrl!, "Approval URL")}
        />
      )}

      {/* Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <GlassCard>
          <div className="flex items-center gap-2 mb-4">
            <StatusIcon status={rel.status} />
            <h3 className="text-h3 font-display text-sc-navy">
              Relationship Summary
            </h3>
          </div>
          <div className="space-y-0">
            <InfoRow label="Customer" value={rel.customerTenantName} />
            <InfoRow label="Tenant ID" value={rel.customerTenantId} mono />
            <InfoRow label="Relationship ID" value={rel.id} mono />
            <div className="flex justify-between py-2 border-b border-sc-border/50">
              <span className="text-body-sm text-sc-text-muted">Status</span>
              <StatusBadge status={rel.status} category="gdap" />
            </div>
            <div className="flex justify-between py-2 border-b border-sc-border/50">
              <span className="text-body-sm text-sc-text-muted">
                Access Type
              </span>
              <StatusBadge status={rel.accessType} category="accessType" />
            </div>
            <InfoRow label="Service Tier" value={rel.serviceTier} />
            <InfoRow label="Duration" value={`${rel.durationDays} days`} />
            <InfoRow
              label="Created"
              value={
                rel.createdDateTime
                  ? new Date(rel.createdDateTime).toLocaleDateString()
                  : null
              }
            />
            <InfoRow
              label="Expires"
              value={
                rel.endDateTime
                  ? new Date(rel.endDateTime).toLocaleDateString()
                  : null
              }
            />
            {rel.expiryDaysRemaining !== null && rel.expiryDaysRemaining !== undefined && rel.expiryDaysRemaining > 0 && (
              <InfoRow
                label="Days Remaining"
                value={rel.expiryDaysRemaining}
              />
            )}
            <InfoRow
              label="Last Reconciliation"
              value={
                rel.lastReconciliation
                  ? new Date(rel.lastReconciliation).toLocaleString()
                  : "Never"
              }
            />
          </div>
        </GlassCard>

        {/* Access Assignments */}
        <AssignmentsSection assignments={rel.accessAssignments} status={rel.status} />
      </div>

      {/* Drift Status */}
      {rel.driftStatus.unresolvedCount > 0 && (
        <DriftSection driftStatus={rel.driftStatus} />
      )}
    </div>
  );
}

function AssignmentsSection({
  assignments,
  status,
}: {
  readonly assignments: GdapRelationshipDetail["accessAssignments"];
  readonly status: string;
}) {
  // Filter out CreationFailed duplicates when an Active sibling exists for the same group
  const visibleAssignments = useMemo(() => {
    const activeGroupIds = new Set(
      assignments
        .filter((a) => a.status === "Active")
        .map((a) => a.securityGroupId),
    );
    return assignments.filter(
      (a) =>
        a.status !== "CreationFailed" ||
        !activeGroupIds.has(a.securityGroupId),
    );
  }, [assignments]);

  const hiddenCount = assignments.length - visibleAssignments.length;

  return (
    <GlassCard>
      <h3 className="text-h3 font-display text-sc-navy mb-4">
        Access Assignments
        <span className="text-body-sm font-normal text-sc-text-muted ml-2">
          ({visibleAssignments.length})
        </span>
      </h3>
      {visibleAssignments.length === 0 ? (
        <div className="text-center py-8">
          <Shield size={32} className="mx-auto mb-2 text-sc-text-muted" />
          <p className="text-body-sm text-sc-text-muted">
            {status === "Approving"
              ? "Assignments will be provisioned after the customer approves."
              : "No access assignments found."}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {visibleAssignments.map((assignment) => (
            <AssignmentCard key={assignment.id} assignment={assignment} />
          ))}
          {hiddenCount > 0 && (
            <p className="text-label text-sc-text-muted text-center pt-1">
              {hiddenCount} superseded assignment{hiddenCount !== 1 ? "s" : ""} hidden
            </p>
          )}
        </div>
      )}
    </GlassCard>
  );
}

function ApprovalBanner({
  approvalUrl,
  onCopy,
}: {
  readonly approvalUrl: string;
  readonly onCopy: () => void;
}) {
  return (
    <GlassCard className="border-blue-500/30 bg-blue-900/10">
      <div className="flex items-start gap-3">
        <Clock size={20} className="text-blue-400 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <h4 className="text-body font-medium text-blue-300 mb-1">
            Awaiting Customer Approval
          </h4>
          <p className="text-body-sm text-sc-text-muted mb-3">
            Send this link to the customer admin so they can approve the GDAP
            relationship in the Microsoft 365 Admin Center.
          </p>
          <div className="flex items-center gap-2 p-2 rounded-input bg-sc-bg-subtle border border-sc-border">
            <span className="text-label font-mono text-sc-text truncate flex-1">
              {approvalUrl}
            </span>
            <button
              onClick={onCopy}
              className="shrink-0 p-1.5 rounded hover:bg-sc-surface-hover transition-colors"
              title="Copy URL"
            >
              <Copy size={14} className="text-sc-text-muted" />
            </button>
            <a
              href={approvalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 p-1.5 rounded hover:bg-sc-surface-hover transition-colors"
              title="Open in new tab"
            >
              <ExternalLink size={14} className="text-sc-text-muted" />
            </a>
          </div>
        </div>
      </div>
    </GlassCard>
  );
}

function AssignmentCard({
  assignment,
}: {
  readonly assignment: GdapRelationshipDetail["accessAssignments"][number];
}) {
  return (
    <div className="rounded-card border border-sc-border bg-sc-surface-hover/30 p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Shield size={14} className="text-sc-primary" />
          <span className="text-body-sm font-medium text-sc-text">
            {assignment.securityGroupName}
          </span>
        </div>
        <StatusBadge status={assignment.status} category="gdap" />
      </div>
      <div className="text-label text-sc-text-muted mb-2">
        {assignment.accessLevel} &middot; {assignment.roles.length} role
        {assignment.roles.length !== 1 ? "s" : ""}
      </div>
      <div className="flex flex-wrap gap-1">
        {assignment.roles.map((roleId) => {
          const displayName =
            ENTRA_ROLE_DISPLAY_NAMES[
              roleId as keyof typeof ENTRA_ROLE_DISPLAY_NAMES
            ] ?? roleId;
          return (
            <span
              key={roleId}
              className="inline-block px-2 py-0.5 text-badge rounded-full bg-sc-primary/10 text-sc-primary"
              title={roleId}
            >
              {displayName}
            </span>
          );
        })}
      </div>
    </div>
  );
}

function DriftResolutionBadge({ resolution }: { readonly resolution: string }) {
  const styles: Record<string, string> = {
    auto_fixed: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
    acknowledged: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
    requires_admin: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  };
  const labels: Record<string, string> = {
    auto_fixed: "Auto-fixed",
    acknowledged: "Acknowledged",
    requires_admin: "Requires Admin",
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-badge font-medium ${styles[resolution] ?? "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400"}`}>
      {labels[resolution] ?? resolution}
    </span>
  );
}

const DRIFT_PAGE_SIZE = 10;

function DriftSection({
  driftStatus,
}: {
  readonly driftStatus: GdapRelationshipDetail["driftStatus"];
}) {
  const [page, setPage] = useState(1);
  const totalPages = Math.ceil(driftStatus.drifts.length / DRIFT_PAGE_SIZE);
  const pagedDrifts = driftStatus.drifts.slice(
    (page - 1) * DRIFT_PAGE_SIZE,
    page * DRIFT_PAGE_SIZE,
  );

  return (
    <GlassCard>
      <div className="flex items-center gap-2 mb-4">
        <AlertTriangle size={18} className="text-amber-500 dark:text-amber-400" />
        <h3 className="text-h3 font-display text-sc-navy">
          Configuration Drift
          {driftStatus.unresolvedCount > 0 && (
            <span className="text-body-sm font-normal text-amber-600 dark:text-amber-400 ml-2">
              ({driftStatus.unresolvedCount} unresolved)
            </span>
          )}
        </h3>
      </div>
      <div className="space-y-2">
        {pagedDrifts.map((drift) => (
          <div
            key={drift.id}
            className="p-3 rounded-card border border-sc-border bg-sc-surface-hover/30"
          >
            <div className="flex items-center justify-between mb-1">
              <span className="text-body-sm font-medium text-sc-text">
                {drift.driftType.replace(/_/g, " ")}
              </span>
              <span className="text-label text-sc-text-muted">
                {new Date(drift.detectedAt).toLocaleString()}
              </span>
            </div>
            <p className="text-body-sm text-sc-text-muted">
              {drift.description}
            </p>
            {drift.resolution && (
              <div className="mt-2">
                <DriftResolutionBadge resolution={drift.resolution} />
              </div>
            )}
          </div>
        ))}
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-sc-border/50">
          <span className="text-label text-sc-text-muted">
            Showing {(page - 1) * DRIFT_PAGE_SIZE + 1}–
            {Math.min(page * DRIFT_PAGE_SIZE, driftStatus.drifts.length)} of{" "}
            {driftStatus.drifts.length}
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 rounded hover:bg-sc-surface-hover transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label="Previous page"
            >
              <ChevronLeft size={16} className="text-sc-text-muted" />
            </button>
            <span className="text-label text-sc-text-muted px-2">
              {page} / {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-1.5 rounded hover:bg-sc-surface-hover transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label="Next page"
            >
              <ChevronRight size={16} className="text-sc-text-muted" />
            </button>
          </div>
        </div>
      )}
    </GlassCard>
  );
}
